/*
 * VigorHair Micro-Infusion product template — shared behaviour.
 * Vanilla JS, no dependencies. Every listener is attached through a small
 * "init once" guard (data-mi-bound) so re-running init() when the Theme
 * Editor hot-reloads a section never double-binds events.
 */
(function () {
  'use strict';

  function bindOnce(el, key, fn) {
    if (!el || el.dataset['miBound' + key]) return;
    el.dataset['miBound' + key] = '1';
    fn();
  }

  /* ---------------------------------------------------------------------
   * Accordions (objections + FAQ) — native <details>/<summary> semantics
   * are used in markup, this just guarantees only one panel per group is
   * open when a section requests exclusive mode.
   * ------------------------------------------------------------------- */
  function initAccordions(root) {
    (root || document).querySelectorAll('[data-mi-accordion]').forEach(function (group) {
      bindOnce(group, 'Accordion', function () {
        var exclusive = group.getAttribute('data-mi-accordion-exclusive') === 'true';
        group.querySelectorAll('.mi-accordion-item').forEach(function (item) {
          item.addEventListener('toggle', function () {
            if (!exclusive || !item.open) return;
            group.querySelectorAll('.mi-accordion-item').forEach(function (other) {
              if (other !== item) other.open = false;
            });
          });
        });
      });
    });
  }

  /* ---------------------------------------------------------------------
   * Hero / gallery thumbnail switcher
   * ------------------------------------------------------------------- */
  function initGalleries(root) {
    (root || document).querySelectorAll('[data-mi-gallery]').forEach(function (gallery) {
      bindOnce(gallery, 'Gallery', function () {
        var main = gallery.querySelector('[data-mi-gallery-main]');
        var thumbs = gallery.querySelectorAll('[data-mi-gallery-thumb]');
        thumbs.forEach(function (thumb) {
          thumb.addEventListener('click', function () {
            var targetHtml = thumb.querySelector('[data-mi-thumb-media]');
            if (main && targetHtml) {
              main.innerHTML = targetHtml.innerHTML;
            }
            thumbs.forEach(function (t) {
              t.setAttribute('aria-pressed', t === thumb ? 'true' : 'false');
            });
          });
        });
      });
    });
  }

  /* ---------------------------------------------------------------------
   * Bundle / offer selector — drives Shopify's native variant input.
   * Each .mi-bundle-option button carries data-variant-id. Selecting one
   * sets the hidden variant id input inside the real product form and
   * updates the displayed price/compare-at/save copy from data attrs
   * (all sourced from Liquid, so figures always match Shopify data).
   * ------------------------------------------------------------------- */
  function initOfferSelectors(root) {
    (root || document).querySelectorAll('[data-mi-offer]').forEach(function (offer) {
      bindOnce(offer, 'Offer', function () {
        var options = offer.querySelectorAll('.mi-bundle-option');
        var variantInput = offer.querySelector('[data-mi-variant-input]');
        var priceNow = offer.querySelector('[data-mi-price-now]');
        var priceWas = offer.querySelector('[data-mi-price-was]');
        var saveBadge = offer.querySelector('[data-mi-save-badge]');
        var ctaLabel = offer.querySelector('[data-mi-cta-label]');
        var stickyPrice = document.querySelector('[data-mi-sticky-price]');
        var stickySub = document.querySelector('[data-mi-sticky-sub]');
        var qtyInput = offer.querySelector('[data-mi-qty-input]');
        var form = offer.querySelector('form[data-mi-product-form]');

        function money(cents) {
          return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
        }

        function applyOption(option, focus) {
          options.forEach(function (o) {
            o.setAttribute('aria-pressed', o === option ? 'true' : 'false');
          });
          if (!option) return;
          var variantId = option.getAttribute('data-variant-id');
          var price = parseInt(option.getAttribute('data-price') || '0', 10);
          var compareAt = parseInt(option.getAttribute('data-compare-at') || '0', 10);
          var title = option.getAttribute('data-title') || '';
          var each = option.getAttribute('data-each') || '';

          if (variantInput && variantId) variantInput.value = variantId;
          if (priceNow) priceNow.textContent = money(price * (qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1));
          if (priceWas) {
            if (compareAt > price) {
              priceWas.textContent = money(compareAt * (qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1));
              priceWas.hidden = false;
            } else {
              priceWas.hidden = true;
            }
          }
          if (saveBadge) {
            var pct = compareAt > price ? Math.round(((compareAt - price) / compareAt) * 100) : 0;
            if (pct > 0) {
              saveBadge.textContent = 'SAVE ' + pct + '%';
              saveBadge.hidden = false;
            } else {
              saveBadge.hidden = true;
            }
          }
          if (ctaLabel) {
            var pctLabel = compareAt > price ? Math.round(((compareAt - price) / compareAt) * 100) : 0;
            ctaLabel.textContent = pctLabel > 0
              ? offer.getAttribute('data-mi-cta-base') + ' — SAVE ' + pctLabel + '%'
              : offer.getAttribute('data-mi-cta-base');
          }
          if (stickyPrice) stickyPrice.textContent = money(price);
          if (stickySub) stickySub.textContent = title + (each ? ' · ' + each : '');
          if (focus) option.focus();
        }

        options.forEach(function (option) {
          option.addEventListener('click', function () { applyOption(option, false); });
        });

        if (qtyInput) {
          qtyInput.addEventListener('change', function () {
            var current = offer.querySelector('.mi-bundle-option[aria-pressed="true"]');
            if (current) applyOption(current, false);
          });
        }

        var initial = offer.querySelector('.mi-bundle-option[aria-pressed="true"]') || options[0];
        applyOption(initial, false);

        offer.querySelectorAll('[data-mi-qty-dec]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            if (!qtyInput) return;
            var v = Math.max(1, (parseInt(qtyInput.value, 10) || 1) - 1);
            qtyInput.value = v;
            qtyInput.dispatchEvent(new Event('change'));
          });
        });
        offer.querySelectorAll('[data-mi-qty-inc]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            if (!qtyInput) return;
            var v = Math.min(99, (parseInt(qtyInput.value, 10) || 1) + 1);
            qtyInput.value = v;
            qtyInput.dispatchEvent(new Event('change'));
          });
        });
      });
    });
  }

  /* ---------------------------------------------------------------------
   * Sticky mobile add-to-cart bar — shows once the real buy box has
   * scrolled out of view, hides again near the footer.
   * ------------------------------------------------------------------- */
  function initStickyBar() {
    var bar = document.querySelector('[data-mi-sticky-bar]');
    if (!bar) return;
    bindOnce(bar, 'StickyBar', function () {
      var target = document.getElementById(bar.getAttribute('data-mi-watch') || 'mi-offer-cta');
      if (!target) return;

      function update() {
        if (window.innerWidth >= 750) {
          bar.classList.remove('mi-sticky-bar--visible');
          return;
        }
        var rect = target.getBoundingClientRect();
        var past = rect.bottom < 0;
        bar.classList.toggle('mi-sticky-bar--visible', past);
      }
      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();

      var submitBtn = bar.querySelector('[data-mi-sticky-submit]');
      if (submitBtn) {
        submitBtn.addEventListener('click', function () {
          var explicitId = submitBtn.getAttribute('data-mi-form-id');
          var form = explicitId
            ? document.getElementById(explicitId)
            : document.querySelector('form[data-type="add-to-cart-form"]');
          if (form && form.requestSubmit) {
            form.requestSubmit();
          } else if (form) {
            form.submit();
          }
        });
      }
    });
  }

  /* ---------------------------------------------------------------------
   * Sticky header — mobile menu toggle + scrolled-state shadow.
   * ------------------------------------------------------------------- */
  function initHeader(root) {
    (root || document).querySelectorAll('[data-mi-header]').forEach(function (header) {
      bindOnce(header, 'Header', function () {
        var toggle = header.querySelector('[data-mi-nav-toggle]');
        var menu = header.querySelector('[data-mi-mobile-nav]');
        if (toggle && menu) {
          toggle.addEventListener('click', function () {
            var open = menu.hasAttribute('hidden') === false;
            if (open) {
              menu.setAttribute('hidden', '');
              toggle.setAttribute('aria-expanded', 'false');
            } else {
              menu.removeAttribute('hidden');
              toggle.setAttribute('aria-expanded', 'true');
            }
          });
          menu.querySelectorAll('a').forEach(function (link) {
            link.addEventListener('click', function () {
              menu.setAttribute('hidden', '');
              toggle.setAttribute('aria-expanded', 'false');
            });
          });
        }

        function updateScrolled() {
          header.classList.toggle('mi-header--scrolled', window.scrollY > 4);
        }
        window.addEventListener('scroll', updateScrolled, { passive: true });
        updateScrolled();
      });
    });
  }

  /* ---------------------------------------------------------------------
   * Reveal-on-scroll for sections marked data-mi-reveal
   * ------------------------------------------------------------------- */
  function initReveal() {
    if (!('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('mi-revealed');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.08 });
    document.querySelectorAll('[data-mi-reveal]:not(.mi-revealed)').forEach(function (el) {
      io.observe(el);
    });
  }

  function init(root) {
    initHeader(root);
    initAccordions(root);
    initGalleries(root);
    initOfferSelectors(root);
    initStickyBar();
    initReveal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  /* Re-init affected section only when the Theme Editor swaps its markup,
   * so editors see accordions/galleries/offer selectors work immediately
   * without a full page reload. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
  document.addEventListener('shopify:section:select', function (event) {
    init(event.target);
  });
})();
